import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Columns, Element } from 'react-bulma-components'
import InputField from '../form/fields/input'
import SelectField from '../form/fields/select'
import CheckboxField from '../form/fields/checkbox'
import {
  countriesAsSelectioOptions,
  phonePrefixesAsSelectionOptions,
} from '../../constants/countries'
import { Translate } from '../../utils/translations'
import { campaignApi } from '../../campaigns/api'
import { omit } from 'lodash'

interface RepairKitRequestFormProps {
  initialEmail?: string
  initialCountry?: string
  /** Campaign-specific quantity map (e.g. {red, black} or {sd500, emd500e}). */
  quantity?: Record<string, number>
  onSuccess: (data: any) => void
  language: string
  /** Raw values entered on a previous visit, used to pre-fill the form. */
  savedValues?: Record<string, any>
  /** Persists the raw form values for back/forward navigation. */
  onSaveValues?: (values: Record<string, any>) => void
}

export interface RepairKitRequestFormValues {
  email: string
  firstName: string
  lastName: string
  address: string
  additionalAddress: string
  zip: string
  city: string
  country: string
  phonePrefix: string
  phone: string
  /** Must be checked before the kit request can be submitted. */
  readConfirmation: boolean
}

const initialFormValues = (email = '', country = ''): RepairKitRequestFormValues => ({
  email,
  firstName: '',
  lastName: '',
  address: '',
  additionalAddress: '',
  zip: '',
  city: '',
  country: country || 'FR',
  phonePrefix: '',
  phone: '',
  readConfirmation: false,
})

const validate = (values: RepairKitRequestFormValues) => {
  const errors: any = {}
  console.log(values)
  if (!values.email) {
    errors.email = <Translate value="fields.required" />
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = <Translate value="fields.validations.address" />
  }
  if (!values.firstName) errors.firstName = <Translate value="fields.required" />
  if (!values.lastName) errors.lastName = <Translate value="fields.required" />
  if (!values.address) errors.address = <Translate value="fields.required" />
  if (!values.zip) errors.zip = <Translate value="fields.required" />
  if (!values.city) errors.city = <Translate value="fields.required" />
  if (!values.country) errors.country = <Translate value="fields.required" />
  if (!values.phonePrefix) errors.phonePrefix = <Translate value="fields.required" />
  if (!values.phone) errors.phone = <Translate value="fields.required" />
  if (!values.readConfirmation) {
    errors.readConfirmation = <Translate value="fields.required" />
  }

  return errors
}

const RepairKitRequestForm: React.FC<RepairKitRequestFormProps> = ({
  initialEmail = '',
  initialCountry = '',
  quantity = {},
  onSuccess,
  language,
  savedValues,
  onSaveValues,
}) => {
  // Pre-fill from saved values when returning to the step. The email stays locked
  // to the requestor email identified earlier, so it always wins over a draft.
  const formInitialValues: RepairKitRequestFormValues = {
    ...initialFormValues(initialEmail, initialCountry),
    ...(savedValues as Partial<RepairKitRequestFormValues> | undefined),
    email: initialEmail || (savedValues?.email ?? ''),
  }
  const useCreateKitRequest = campaignApi.useCreateKitRequest
  const mutation = useCreateKitRequest({
    mutation: {
      onSuccess: data => {
        onSuccess(data)
      },
    },
  })

  const handleSubmit = (values: RepairKitRequestFormValues) => {
    // Persist the raw values so the step is pre-filled when navigating back.
    onSaveValues?.(values)

    // readConfirmation is a UI-only consent gate; it must not be sent to the API.
    const requestorFields = omit(values, 'readConfirmation')

    // The quantity comes from the saved product-quantity record, so it may carry
    // a Mongoose-added `_id`. Strip it: the backend whitelist forbids unknown
    // properties on the quantity payload.
    const cleanQuantity = { ...quantity }
    delete (cleanQuantity as Record<string, any>)._id

    mutation.mutate({
      data: {
        requestor: {
          ...requestorFields,
          language,
        },
        // Forward only the campaign-specific quantity numbers.
        quantity: cleanQuantity,
      },
    })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={formInitialValues}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Field
            name="email"
            component={InputField}
            type="email"
            label={<Translate value="fields.email" />}
            disabled={!!initialEmail}
          />
          <Field
            name="firstName"
            component={InputField}
            type="text"
            label={<Translate value="fields.lastName" />}
          />
          <Field
            name="lastName"
            component={InputField}
            type="text"
            label={<Translate value="fields.firstName" />}
          />
          <Field
            name="address"
            component={InputField}
            type="text"
            label={<Translate value="fields.deliveryAddress" />}
          />
          <Field
            name="additionalAddress"
            component={InputField}
            type="text"
            label={<Translate value="fields.additionalAddress" />}
          />
          <Field
            name="zip"
            component={InputField}
            type="text"
            label={<Translate value="fields.zip" />}
          />
          <Field
            name="city"
            component={InputField}
            type="text"
            label={<Translate value="fields.city" />}
          />
          <Field
            name="country"
            component={SelectField}
            label={<Translate value="fields.country" />}
            options={countriesAsSelectioOptions}
            selectDefaultChooseOptionLabel={<Translate value="fields.choose" />}
          />
          <Columns>
            <Columns.Column size={4}>
              <Field
                name="phonePrefix"
                component={SelectField}
                label={<Translate value="fields.phonePrefix" />}
                options={phonePrefixesAsSelectionOptions}
                selectDefaultChooseOptionLabel={<Translate value="fields.choose" />}
              />
            </Columns.Column>
            <Columns.Column size={8}>
              <Field
                name="phone"
                component={InputField}
                type="tel"
                label={<Translate value="fields.phone" />}
              />
            </Columns.Column>
          </Columns>
          <Field
            name="readConfirmation"
            component={CheckboxField}
            label={<Translate value="fields.readConfirmation" />}
          />
          <Element mt={4} textAlign="center">
            <Button
              color="primary"
              type="submit"
              disabled={isSubmitting}
              data-testid="step-submit"
            >
              <Translate value="form.submit" />
            </Button>
          </Element>
        </Form>
      )}
    </Formik>
  )
}

export default RepairKitRequestForm
